/**
 * OnAirNowTicker — compact live-radio activity strip.
 *
 * Pulls /api/radio/now-playing for both AI and Music stations, cycles
 * between them, and shows a "ON-AIR NOW · <Track> — <Artist>" ticker
 * with a pulsing red dot. Clicking the ticker takes the visitor to the
 * relevant station's page, doubling as a soft funnel into the radios.
 *
 * No auth required — works for logged-out and logged-in users alike.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Radio, Sparkles, Music as MusicIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 30_000;
const CYCLE_MS = 7_000;

const STATIONS = [
  { id: 'all', label: 'All Genre Radio', icon: Radio, route: '/radio?station=all', accent: '#FFDA33' },
  { id: 'music', label: 'Music Radio', icon: MusicIcon, route: '/radio?station=music', accent: '#00CCFF' },
  { id: 'ai', label: 'AI Radio', icon: Sparkles, route: '/radio?station=ai', accent: '#9b59b6' },
  { id: 'beats', label: 'Beats Radio', icon: MusicIcon, route: '/radio?station=beats', accent: '#ff6b6b' },
];

export default function OnAirNowTicker({ fixed = false, topOffset = '72px' }) {
  const [byStation, setByStation] = useState({});
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  // Fetch both stations in parallel, then re-poll every 30s
  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const results = await Promise.all(
          STATIONS.map((s) =>
            axios.get(`${API}/radio/now-playing?station=${s.id}`).then((r) => [s.id, r.data]).catch(() => [s.id, null])
          )
        );
        if (!mountedRef.current) return;
        const next = {};
        for (const [id, data] of results) next[id] = data;
        setByStation(next);
      } catch {
        // Silent — ticker will just stay idle
      }
    };
    load();
    const poll = setInterval(load, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(poll);
    };
  }, []);

  // Cycle between AI and Music every 7s
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % STATIONS.length), CYCLE_MS);
    return () => clearInterval(t);
  }, []);

  const active = STATIONS[idx];
  const data = byStation[active.id];
  const track = data?.current_track;
  const peak = !!data?.peak_hours;
  const vipQueued = !!data?.pending_vip_request;
  const ActiveIcon = active.icon;

  if (!track) {
    // Don't render until we have something to show (keeps layout clean)
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigate(active.route)}
      className={
        (fixed
          ? 'fixed left-0 right-0 z-40 bg-black '
          : 'w-full bg-black ')
        + 'group flex items-center gap-3 px-4 md:px-6 py-2.5 border-y border-[#1a1a1a] hover:bg-[#0f0f0f] transition-colors overflow-hidden'
      }
      style={fixed ? { top: topOffset } : undefined}
      data-testid="on-air-now-ticker"
      aria-label={`On air now on ${active.label}: ${track.title} by ${track.artist || 'unknown'}. Tap to tune in.`}
    >
      {/* LIVE dot */}
      <span className="relative inline-flex shrink-0" aria-hidden="true">
        <span className="absolute inline-flex h-2 w-2 rounded-full bg-red-500 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
      </span>

      {/* Station badge */}
      <span
        className="shrink-0 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border"
        style={{ color: active.accent, borderColor: `${active.accent}55`, background: `${active.accent}10` }}
      >
        <ActiveIcon size={11} />
        {active.label}
      </span>

      <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hidden sm:inline">
        ON AIR NOW
      </span>

      {/* Scrolling track label */}
      <span className="flex-1 min-w-0 overflow-hidden">
        <span
          className="block truncate text-white text-sm font-['Chakra_Petch'] group-hover:text-[#FFDA33] transition-colors"
          data-testid="on-air-track-label"
        >
          <span className="font-semibold">{track.title}</span>
          {track.artist && <span className="text-[#888]"> — {track.artist}</span>}
        </span>
      </span>

      {/* Peak / VIP queue badges */}
      {vipQueued && (
        <span className="hidden md:inline-flex items-center gap-1 text-[9px] font-mono uppercase text-[#FFDA33] bg-[#FFDA33]/15 border border-[#FFDA33]/40 px-1.5 py-0.5 rounded">
          🎙 VIP queued
        </span>
      )}
      {peak && (
        <span className="hidden lg:inline text-[9px] font-mono uppercase text-[#00ff88] bg-[#00ff88]/15 px-1.5 py-0.5 rounded">
          PEAK
        </span>
      )}

      {/* Station cycle dots */}
      <span className="shrink-0 flex items-center gap-1" aria-hidden="true">
        {STATIONS.map((s, i) => (
          <span
            key={s.id}
            className="h-1 w-1 rounded-full transition-colors"
            style={{ background: i === idx ? active.accent : '#333' }}
          />
        ))}
      </span>

      <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-[#666] group-hover:text-[#FFDA33] transition-colors flex items-center gap-1">
        <Radio size={11} /> Tune in
      </span>
    </button>
  );
}
